import { Metamodel } from 'app/model/base/model';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { FLOW } from 'app/model/adapters/dataflow/IDataflow';
import { TransformationAdapter } from 'app/model/adapters/dataflow/transformations/base-transformation.adapter';
import { DataFlowSchemaAdapterUtils } from '../dataflow-schema-adapter-utils';
import { FoldDownTransformation } from 'app/model/dataflow/folddowntransformation';
import { DataFlowExpressionUtils } from '../expression.util';
import { AnyDataFlowType, BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { ExpressionBasedColumnVisitorAdapterUtils } from '../expression-based-column-visitor-adapter.utils';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(FoldDownTransformation),
    asynchronous: true
})
export class FoldDownTransformationAdapter extends TransformationAdapter<FoldDownTransformation> {
    public async getDslScript(foldDownTransformation: FoldDownTransformation): Promise<string> {
        const props: string[] = [];

        const columns = DataFlowSchemaAdapterUtils.getColumnsDsl(foldDownTransformation);
        const partition = TransformationAdapter.getPartitionDslScript(foldDownTransformation);

        foldDownTransformation.unrollBy =
            foldDownTransformation.unrollMultiple[0] instanceof DataFlowExpression
                ? foldDownTransformation.unrollMultiple[0]
                : foldDownTransformation.createTemplatableExpression(AnyDataFlowType.TYPE, '');

        if (foldDownTransformation.unrollBy) {
            const unrollRoot = foldDownTransformation.unrollRoot.expression
                ? DataFlowExpressionUtils.wrapExpressionConfigValue(
                      foldDownTransformation.unrollRoot,
                      AnyDataFlowType.TYPE
                  )
                : null;
            props.push(
                `unroll(${DataFlowExpressionUtils.wrapExpressionConfigValue(
                    foldDownTransformation.unrollBy,
                    AnyDataFlowType.TYPE
                )}${unrollRoot ? `, ${unrollRoot}` : ''})`
            );
        }

        if (foldDownTransformation.unrollMultiple.length > 1 || foldDownTransformation.columnPatterns.length) {
            props.pop();
            const unrollMultipleExpressions: string[] = [];
            foldDownTransformation.unrollMultiple.forEach((unrollBy) => {
                if (unrollBy instanceof DataFlowExpression) {
                    unrollMultipleExpressions.push(
                        DataFlowExpressionUtils.wrapExpressionConfigValue(unrollBy, AnyDataFlowType.TYPE)
                    );
                }
            });
            const columnPatternExpression = await ExpressionBasedColumnVisitorAdapterUtils.writeToDsl(
                this.context.contextData,
                foldDownTransformation.columnPatterns,
                foldDownTransformation
            );
            if (columnPatternExpression) {
                unrollMultipleExpressions.push(columnPatternExpression);
            }
            props.push(`unrollMultiple(${unrollMultipleExpressions.join(',')})`);
        }

        if (columns) {
            props.push(columns);
        }

        if (partition) {
            props.push(partition);
        }
        props.push(
            `skipDuplicateMapInputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                foldDownTransformation.skipDuplicateMapInputs,
                BooleanDataFlowType.TYPE
            )}`
        );
        props.push(
            `skipDuplicateMapOutputs: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                foldDownTransformation.skipDuplicateMapOutputs,
                BooleanDataFlowType.TYPE
            )}`
        );

        const knownProps = props.filter((prop) => !!prop).join(',\n\t');
        const unknownProps = this._getUnknownProps(foldDownTransformation, knownProps);
        const transformationData = [knownProps, unknownProps].filter((c) => !!c).join(',\n\t');

        return (
            `${this._getInputStreams(foldDownTransformation)} ${FoldDownTransformation.MODEL.name}` +
            `(${transformationData}) ${FLOW} ${this._getOutputStreams(foldDownTransformation)}`
        );
    }
}
